import { probe } from "../perf/probe";
import initModule, * as raw from "./pkg/gr_wasm.js";
import type {
  DecodedUnreliable,
  InputCmdData,
  PlayerView,
  PredictedFrame,
  RayHitData,
  ReconcileResult,
  RenderMeshData,
  Vec3Like,
} from "./types";

export { Buttons, PlayerFlags, SurfaceFlags } from "./types";
export type {
  BombSnapData,
  BombStateData,
  DecodedUnreliable,
  FireSampleData,
  FxEventData,
  HillSnapData,
  InputCmdData,
  LocalPlayerData,
  MatchPhaseName,
  MeleeEventData,
  ModeSnapData,
  MoveEventsData,
  PlayerSnapData,
  PlayerView,
  PongData,
  PredictedFrame,
  PredictedShot,
  ProjectileSnapData,
  RayHitData,
  ReconcileResult,
  RenderMeshData,
  RenderMeshGroup,
  SnapshotData,
  TeamName,
  ThrowEventData,
  Vec3Like,
  WeaponActionName,
  WeaponEventsData,
} from "./types";

let initPromise: Promise<void> | null = null;

/** Loads and instantiates the WASM module. Idempotent; concurrent callers share one load. */
export function initWasm(): Promise<void> {
  if (!initPromise) {
    initPromise = initModule().then(
      (exports) => {
        probe?.expose("wasmMemory", exports.memory);
      },
      (e: unknown) => {
        initPromise = null;
        throw e;
      },
    );
  }
  return initPromise;
}

/** A parsed map: collision world, render mesh and hash. Call `free()` when done. */
export class WorldHandle {
  private constructor(private readonly inner: raw.WorldHandle) {}

  /** Parses and compiles a map file; throws on an invalid map. */
  static fromJson(json: string): WorldHandle {
    return new WorldHandle(raw.WorldHandle.fromJson(json));
  }

  /** @internal The wasm object, for `Predictor`. */
  get handle(): raw.WorldHandle {
    return this.inner;
  }

  /** Render geometry per material (copies; independent of the handle). */
  renderMesh(): RenderMeshData {
    const mesh: RenderMeshData = this.inner.renderMesh();
    return mesh;
  }

  /** Nearest shot-mask hit along the ray (`d` need not be normalized), or undefined. */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): RayHitData | undefined {
    const hit: RayHitData | undefined = this.inner.raycast(ox, oy, oz, dx, dy, dz, maxDist);
    return hit;
  }

  /**
   * Breaks a breakable brush in server tick `tick` (`GlassBroken`): from the next tick on it blocks
   * nothing. False when it is not breakable or broke already. `Predictor.replay` afterwards.
   */
  breakBrush(brush: number, tick: number): boolean {
    return this.inner.breakBrush(brush, tick);
  }

  /** Every broken brush whole again (`GlassRestored`); false when none was broken. */
  restoreBreakables(): boolean {
    return this.inner.restoreBreakables();
  }

  /** FNV-1a 64 of the map JSON as 16 lowercase hex characters (`Welcome.mapHash`). */
  mapHash(): string {
    return this.inner.mapHash();
  }

  free(): void {
    this.inner.free();
  }
}

/**
 * Prediction and reconciliation of the local player with the shared `gr-core` simulation.
 * `spawn` (from `Spawned`), `step` once per simulated tick, `reconcile` with every snapshot that
 * has a `local` block. Methods throw on malformed input (bad loadout JSON, bad sim bytes).
 */
export class Predictor {
  private readonly inner: raw.Predictor;

  /** `moveConfigJson`: `Welcome.moveConfigJson` (the server's base MoveConfig). */
  constructor(world: WorldHandle, moveConfigJson: string) {
    this.inner = new raw.Predictor(world.handle, moveConfigJson);
  }

  /** Resets prediction to the server state `simBytes` as of the end of `tick`. `loadoutJson` is `toJson(LoadoutSchema, loadout)`. */
  spawn(loadoutJson: string, simBytes: Uint8Array, tick: number): void {
    this.inner.spawn(loadoutJson, simBytes, tick);
  }

  /** Simulates one input; ticks after the previous one are fine, skipped ticks are filled in silently. */
  step(cmd: InputCmdData): PredictedFrame {
    const frame: PredictedFrame = this.inner.step(cmd);
    return frame;
  }

  /** Reconciles with the server state after `tick` (`snapshot.local.sim`). */
  reconcile(tick: number, simBytes: Uint8Array): ReconcileResult {
    const result: ReconcileResult = this.inner.reconcile(tick, simBytes);
    return result;
  }

  /**
   * The world changed under predicted ticks (`WorldHandle.breakBrush`): replays the inputs after the
   * newest confirmed tick. `corrected` is false; the error is the visual offset to smooth out.
   */
  replay(): ReconcileResult {
    const result: ReconcileResult = this.inner.replay();
    return result;
  }

  /** Newest predicted state. */
  view(): PlayerView {
    const view: PlayerView = this.inner.view();
    return view;
  }

  free(): void {
    this.inner.free();
  }
}

/** The closed-loop input lead controller (`gr_core::lead`); see `net/tickClock.ts`. */
export { LeadControl } from "./pkg/gr_wasm.js";

/** Input packet for the unreliable channel: newest input last, trailing contiguous run, at most `MAX_INPUTS_PER_PACKET` (16). */
export function encodeInputPacket(lastSnapshotTick: number, cmds: readonly InputCmdData[]): Uint8Array {
  return raw.encodeInputPacket(lastSnapshotTick, cmds);
}

export function encodePing(clientTimeMs: number): Uint8Array {
  return raw.encodePing(clientTimeMs);
}

/** Decodes a server datagram or unreliable frame; throws on malformed input. */
export function decodeUnreliable(bytes: Uint8Array): DecodedUnreliable {
  const decoded: DecodedUnreliable = raw.decodeUnreliable(bytes);
  return decoded;
}

/** Pellet directions (xyz triples) of a shot; `yaw`/`pitch` in radians, `spreadDeg` the cone half-angle. */
export function shotDirections(
  weaponId: string,
  attachments: readonly string[],
  yaw: number,
  pitch: number,
  spreadDeg: number,
  seed: number,
): Float32Array {
  return raw.shotDirections(weaponId, [...attachments], yaw, pitch, spreadDeg, seed);
}

/**
 * Ray against the hitboxes of one player (the server's hit test): `[distance, zone]` with zone 0
 * head, 1 body, 2 limb, or an empty array for a miss. `dir` need not be normalized. `yaw`, `speed`
 * (horizontal, m/s), `grounded` and `air` (legs tucked for the air, 0..1) place the leaning
 * stances, the run bob and the tucked legs like the server does.
 */
export function rayHitboxes(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  feet: Vec3Like,
  crouchFrac: number,
  sliding: boolean,
  yaw: number,
  speed: number,
  grounded: boolean,
  air: number,
): Float32Array {
  return raw.rayHitboxes(ox, oy, oz, dx, dy, dz, maxDist, feet.x, feet.y, feet.z, crouchFrac, sliding, yaw, speed, grounded, air);
}

/** Resolved weapon stats (attachments and perks folded in) as JSON. */
export function weaponStatsJson(weaponId: string, attachments: readonly string[], perks: readonly string[]): string {
  return raw.weaponStatsJson(weaponId, [...attachments], [...perks]);
}

/** Seed of one shot for `shotDirections`: match seed, shooter id, shooter's shot counter. */
export function shotSeed(matchSeed: number, playerId: number, shotCounter: number): number {
  return raw.shotSeed(matchSeed, playerId, shotCounter);
}
